import { useEffect } from 'react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import React from "react";

import { FaQuestionCircle } from "react-icons/fa";
import { Sparkles } from "lucide-react";

const faqs = [
  {
    question: "What are the investment properties for sale right now?",
    answer: "Our inventory is updated daily. Once you have met with an advisor, you will get real-time notifications about properties for sale that match your unique needs."
  },
  {
    question: "Which types of properties are available?",
    answer: "We provide various property types, from single-family homes to townhouses, duplexes, triplexes quadplexes. We even provide multi-family homes."
  },
  {
    question: "What are typical investor goals when buying from InvestorRise?",
    answer: "It depends on the investor. Many of our investors are interested in reselling properties for profit after fixing them up, while others prefer to rent them out for passive monthly income."
  },
  {
    question: "How does InvestorRise work?",
    answer: "It’s simple. All you do is contact us and schedule a meeting with an advisor. Let us know your investment criteria, and we will start matching properties to your investment goals. Once you find a property, you will sign a contract. Then, the property is yours."
  },
  {
    question: "Why do I need to meet with an advisor?",
    answer: "Our investors are required to meet with an advisor at least once. The meeting helps us better understand who you are and what you want from your real estate investment. It also allows us to explain our process so that you know what to expect. We want to ensure clear communication, provide complete transparency, and have a great long-term relationship."
  },
  {
    question: "Do you own the properties you have for sale? (Are you flipping contracts?)",
    answer: "First, we acquire and close on the properties. Then, we sell them to our real estate investors. That allows us to better control the assets so that closings are smooth for our clients. InvestorRise does not flip contracts."
  }
  ,
  {
    question: "Where do you get your properties from?",
    answer: "InvestorRise has an in-house marketing department that finds deals from a variety of channels. Our properties are not on the MLS and are not available through traditional methods."
  }
  ,
  {
    question: "Why do your properties sell so quickly?",
    answer: "Our deals sell quickly because we provide the best off-market properties at fair prices. We do not believe in bidding up prices, so our clients always know the price they will pay when they want a deal. We partner with serious investors, and they enjoy fair policies, excellent service, and amazing deals."
  }
  ,
  {
    question: "Why does InvestorRise require escrow upfront (before property selection)?",
    answer: "Since we do not believe in bidding wars, the first investor to sign a contract and offer a deposit wins the property for sale. Having an escrow deposit already on file gives you the edge to win the deal faster than the next investor. Escrow also puts you in our qualified/preferred status, so you can be notified about properties before other clients in lower/unqualified status."
  }
];

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.2 } })
};

const AccordionItem = ({ faq, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      variants={fadeIn}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      custom={index}
      className="mb-4 border border-blue-200 rounded-xl overflow-hidden bg-gray-50 shadow-md"
    >
      <button
        className="w-full px-6 py-4 text-left font-semibold text-slate-900 flex justify-between items-center hover:bg-blue-50 transition duration-300"
        onClick={() => setIsOpen(!isOpen)}
      >
        {faq.question}
        <span className={`transform transition-transform text-teal-600 ${isOpen ? "rotate-180" : "rotate-0"}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="px-6 pb-4 text-gray-700 text-sm leading-relaxed">
          {faq.answer}
        </div>
      )}
    </motion.div>
  );
};


export default function FAQs() {
    const [step, setStep] = useState('initial');
  const [selectedOption, setSelectedOption] = useState('');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


 
  return (
    <div className="bg-slate-900">
  {/* Hero Section */}
  <div className="relative md:h-[70vh] h-[40vh] overflow-hidden">
  {/* Background Video with Zoom Out */}
  <motion.div
    initial={{ scale: 1.2 }}
    animate={{ scale: 1 }}
    transition={{ duration: 3, ease: "easeOut" }}
    className="absolute inset-0"
  >
    <video
      className="w-full h-full object-cover"
      autoPlay
      loop
      muted
      playsInline
    >
      <source src="/assets/videos/investor-rise-bg-map.mp4" type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  </motion.div>

  {/* Overlay */}
  <div className="absolute inset-0 bg-black bg-opacity-30" />

  {/* Heading on the Left */}
  <div className="relative z-10 h-full flex items-end sm:items-center justify-start px-5 sm:px-10 pb-10 sm:pb-0 ml-4 md:ml-8">
  <div className="relative inline-block">
  <motion.div
    initial={{ opacity: 0, y: 80 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 2, ease: "easeOut" }}
    className="bg-black bg-opacity-40 rounded-xl shadow-xl px-6 py-4"
  >
    <h1 className="text-white text-5xl sm:text-4xl md:text-7xl font-semibold leading-tight">
      FAQs
    </h1>
  </motion.div>

  <motion.div
    animate={{ y: [0, -14, 3, 0] }}
    transition={{ repeat: Infinity, duration: 3 }}
    className="absolute top-[-20px] left-[-20px] w-[70px] h-[80px] border-t-[22px] border-l-[22px] border-teal-500 opacity-25 "
  />
</div>
    
  </div>
</div>




  <div className="min-h-screen bg-slate-800 p-6">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl md:text-5xl font-bold text-white flex justify-center items-center gap-2">
          <FaQuestionCircle className="text-blue-600" /> Frequently Asked Questions
        </h1>
        
        <p className="text-gray-400 mt-2 max-w-xl mx-auto">
          Find answers to the most common questions below.
        </p>
      </motion.div>

      <div className="max-w-3xl mx-auto">
        {faqs.map((faq, index) => (
          <AccordionItem key={index} faq={faq} index={index} />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="mt-16 flex justify-center"
      >
        <Sparkles className="text-teal-500 animate-pulse w-12 h-12" />
      </motion.div>
    </div>

</div>

  
  );
}
